import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, FileText } from 'lucide-react';
import Logo from './Logo';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Accueil', path: '/' },
    { name: 'Solutions', path: '/services' },
    { name: 'Devis', path: '/devis' },
    { name: 'Contact', path: '/contact' },
    { name: 'FAQ', path: '/faq' }
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md py-3' : 'bg-white shadow-sm py-4'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <Logo />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navigation.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-medium transition-colors hover:text-cyan-500 ${
                    location.pathname === item.path
                      ? 'text-blue-900 font-semibold'
                      : 'text-gray-700'
                  }`}
                  style={{ fontFamily: '"Inter", sans-serif' }}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Desktop CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => window.location.href = 'tel:+33184180342'}
              >
                <Phone className="w-4 h-4" />
                Appeler
              </Button>
              <Button
                size="sm"
                className="gap-2 bg-blue-900 hover:bg-blue-800"
                onClick={() => window.location.href = '/devis'}
              >
                <FileText className="w-4 h-4" />
                Devis gratuit
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-gray-700 hover:text-blue-900 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" style={{ top: '72px' }}>
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="absolute top-0 right-0 w-64 h-full bg-white shadow-xl p-6 pt-8 overflow-y-auto">
            <nav className="flex flex-col gap-4">
              {navigation.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-base font-medium transition-colors py-2 ${
                    location.pathname === item.path
                      ? 'text-blue-900 font-semibold'
                      : 'text-gray-700 hover:text-cyan-500'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  style={{ fontFamily: '"Inter", sans-serif' }}
                >
                  {item.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-gray-200 flex flex-col gap-3">
                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={() => window.location.href = 'tel:+33184180342'}
                >
                  <Phone className="w-4 h-4" />
                  Appeler
                </Button>
                <Button
                  className="w-full gap-2 bg-blue-900 hover:bg-blue-800"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    window.location.href = '/devis';
                  }}
                >
                  <FileText className="w-4 h-4" />
                  Devis gratuit
                </Button>
              </div>
            </nav>
          </div>
        </div>
      )}

      {/* Mobile Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-white border-t border-gray-200 shadow-lg">
        <div className="grid grid-cols-2 gap-2 p-3">
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => window.location.href = 'tel:+33184180342'}
          >
            <Phone className="w-4 h-4" />
            Appeler
          </Button>
          <Button
            className="gap-2 bg-blue-900 hover:bg-blue-800"
            onClick={() => window.location.href = '/devis'}
          >
            <FileText className="w-4 h-4" />
            Devis
          </Button>
        </div>
      </div>
    </>
  );
};

export default Header;
